import { useCallback, useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import { usePersistentState } from "@/contexts/LocalStorage";
import { UPDATE_ORGANISATION_DETAILS_MUTATION } from "@/graphql/organisations/mutations";
import {
  ORGANISATION_LIST_QUERY,
  ORGANISATION_ROLES_QUERY
} from "@/graphql/organisations/queries/organisation";
import { useMutation, useQuery } from "@apollo/client";

import useAvailableOrganisations from "./useAvailableOrganisations";

export default function useOrganisation() {
  const [showCreateOrganisationModal, setShowCreateOrganisationModal] = useState(false);
  const [showUpdateOrganisationNameModal, setShowUpdateOrganisationNameModal] = useState(false);

  const [error, setError] = useState(null);

  const [_organisationId, setOrganisationId] = usePersistentState("selectedOrganisationId", null);

  const params = useParams();

  const organisationId = params.organisationId || _organisationId;

  const [availableOrganisations, { loading: listLoading, refetch }] = useAvailableOrganisations();

  // organisationId from the url params will be a hashId
  const organisation = availableOrganisations?.find(
    org => org.id == organisationId || org.hashId == organisationId
  );

  const { data: { organisationRoles } = {} } = useQuery(ORGANISATION_ROLES_QUERY, {
    variables: {
      organisation: parseInt(organisation?.id)
    },
    skip: !organisation?.id
  });

  const [_updateOrganisationDetails, { loading: updateOrganisationDetailsMutationLoading }] =
    useMutation(UPDATE_ORGANISATION_DETAILS_MUTATION, {
      refetchQueries: [
        {
          query: ORGANISATION_LIST_QUERY
        }
      ]
    });

  const updateOrganisationDetails = useCallback(
    async variables => {
      if (!organisation?.id) {
        return;
      }
      try {
        await _updateOrganisationDetails({
          variables: {
            input: {
              id: parseInt(organisation?.id),
              ...variables
            }
          }
        });
      } catch (e) {
        setError(e);
      }
    },
    [_updateOrganisationDetails, organisation?.id]
  );

  useEffect(() => {
    if (organisation) {
      // store the numerical id in the persistent storage like before
      setOrganisationId(organisation.id);
    }
  }, [organisation, setOrganisationId]);

  const permissions = organisation?.permissions || [];

  const organisationNotFound = availableOrganisations
    ? !!availableOrganisations?.length && !organisation
    : undefined;

  const noOrganisationsAvailable = availableOrganisations
    ? !availableOrganisations.length
    : undefined;

  return {
    organisationNotFound,
    noOrganisationsAvailable,
    loading: listLoading || updateOrganisationDetailsMutationLoading,
    error,
    organisation,
    organisationRoles,
    availableOrganisations,
    organisationId: organisation?.id, // the numerical id
    permissions,
    updateOrganisationDetails,
    refetchOrganisations: refetch,
    showCreateOrganisationModal,
    setShowCreateOrganisationModal,
    showUpdateOrganisationNameModal,
    setShowUpdateOrganisationNameModal
  };
}
